<script setup>
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primevue'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useUIStore } from '@/core/stores/ui.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useAnalytics } from '@/shared/analytics'
import DssModuleTitle from '@/shared/components/navigation/dss-module-title/dss-module-title.vue'
import DssShellModuleContent from '@/shared/components/shells/dss-shell-module-content/dss-shell-module-content.vue'
import { useBradleyEnvironment } from '@/shared/composables/use-bradley-environment.js'
import {
  BRADLEY_ANALYSE_DATA_TOOL_LABEL as ANALYSE_DATA_TOOL,
  BRADLEY_ANALYTICS_EVENTS,
  BRADLEY_FAQ_VIEW_QUESTION_BY_SECTION,
} from '../../analytics/bradley-analytics.constants.js'
import {
  bradleyAnalyticsErrorCategory,
  bradleyExtensionFromFileName,
  bradleyFileTelemetryMeta,
  bradleyToolsSelectedForAnalytics,
  bradleyUploadErrorAnalyticsType,
} from '../../analytics/bradley-analytics.telemetry.js'
import {
  BRADLEY_BLOB_TRANSFORMATION_POLL_INTERVAL_MS,
  clearAllBradleyConversations,
  clearStoredConversationId,
  deleteBradleyConversation,
  fetchBradleyBlobTransformationStatus,
  fetchBradleyConversationsByUser,
  fetchBradleyMessages,
  fetchBradleyUploadedDocuments,
  getStoredConversationId,
  renameBradleyConversationTitle,
  startBradleyConversation,
  storeConversationId,
  streamBradleyMessage,
  uploadBradleyBlobStorageFile,
  uploadBradleyDocument,
} from '../../services/bradley-ai.service.js'
import { loadBradleyProjectsBundle } from '../../services/load-bradley-projects-bundle.js'
import {
  areAllBradleyAttachmentsReadyForSend,
  BRADLEY_ATTACHMENT_STATUS,
  getBradleyAttachmentFileHashForSend,
  getBradleyAttachmentStatus,
  isBradleyAttachmentReadyForSend,
  isBradleyImageFile,
} from '../../utils/bradley-attachment.util.js'
import { buildBradleyAssistantMessage } from '../../utils/bradley-message.util.js'
import { setCookie } from '../../utils/cookies.js'
import BradleyAiPolicyModal from '../bradley-ai-policy-modal.vue'
import BradleyHeader from '../bradley-header.vue'
import BradleyInputArea from '../bradley-input-area.vue'
import BradleyLegalDisclaimerBar from '../bradley-legal-disclaimer-bar.vue'
import BradleyLogoIcon from '../bradley-logo-icon.vue'
import BradleyPromptCards from '../bradley-prompt-cards.vue'
import BradleySearchModal from '../bradley-search-modal.vue'
import BradleySettingsModal from '../bradley-settings-modal.vue'
import BradleySidebarMenu from '../bradley-sidebar-menu.vue'
import BradleyTermsOfServiceModal from '../bradley-terms-of-service-modal.vue'
import ChatFile from '../chat-file.vue'

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const router = useRouter()
const uiStore = useUIStore()
const authStore = useAuthStore()

const TERMS_COOKIE_NAME = 'bradley_terms_accepted'
const TERMS_PANEL_WIDTH = '640px'
const NEW_CHAT_QUERY = 'new'
const { dataAnalysisEnabled: DATA_ANALYSIS_ENABLED, projectsEnabled: PROJECTS_ENABLED, newFileUploadEnabled: NEW_FILE_UPLOAD_ENABLED } = useBradleyEnvironment()
const BradleyProjectWorkspace = PROJECTS_ENABLED
  ? defineAsyncComponent(() => import('../bradley-project-workspace.vue'))
  : null
const projectsBundle = shallowRef(null)
let projectsBundlePromise = null

function loadProjectsBundle() {
  if (!PROJECTS_ENABLED) {
    return Promise.resolve(null)
  }
  if (!projectsBundlePromise) {
    projectsBundlePromise = loadBradleyProjectsBundle().then((bundle) => {
      projectsBundle.value = bundle
      return bundle
    })
  }
  return projectsBundlePromise
}

async function requireProjectsBundle() {
  const bundle = await loadProjectsBundle()
  if (!bundle) {
    throw new Error('Bradley projects are disabled')
  }
  return bundle
}

function runWithProjectsBundle(callback) {
  if (!PROJECTS_ENABLED) {
    return
  }

  if (projectsBundle.value) {
    callback(projectsBundle.value)
    return
  }

  void requireProjectsBundle()
    .then(callback)
    .catch((error) => {
      console.error('Failed to load Bradley projects bundle', error)
    })
}

const ALLOWED_TOOLS = DATA_ANALYSIS_ENABLED ? [ANALYSE_DATA_TOOL] : []
const DEFAULT_SIDEBAR_WIDTH = 72
const { t } = useI18n()
const { trackEvent } = useAnalytics()

const toast = useToast()
const termsOfServiceModalOpen = ref(false)
const aiPolicyModalOpen = ref(false)
const sidebarWidth = ref(DEFAULT_SIDEBAR_WIDTH)
const settingsModalOpen = ref(false)
const searchModalOpen = ref(false)
const userManualModalOpen = ref(false)
const faqOpenSection = ref(null)
const isLoadingSearchConversations = ref(false)
const searchConversations = ref([])

const sidebarRecentsLoading = computed(() => isLoadingSearchConversations.value && !searchModalOpen.value)
const activeProjectId = ref(null)
const activeProjectTitle = ref('')
const activeProjectUpdatedAt = ref(null)
const activeProjectIsOwner = ref(false)
const projectFilesRevision = ref(0)
const autoEditProjectId = ref(null)
const isCreatingProject = ref(false)
const sidebarMenuReference = ref(null)
const projectWorkspaceReference = ref(null)
const isProjectWorkspaceOpen = computed(() => PROJECTS_ENABLED && Boolean(activeProjectId.value))
const activeProjectFiles = computed(() => {
  void projectFilesRevision.value
  if (!activeProjectId.value || !projectsBundle.value) {
    return []
  }
  return projectsBundle.value.getCachedBradleyProjectFiles(activeProjectId.value)
})
const isDeletingConversation = ref(false)
const selectedPrompt = ref('')
const selectedTools = ref([])
const showChatFile = ref(false)
const showProjectWorkspace = computed(() => isProjectWorkspaceOpen.value && !showChatFile.value)
const uploadedFile = ref(null)
const lastMessage = ref('')
const conversationId = ref(null)
const messages = ref([])
const activeConversationTitle = ref('')
const isAwaitingResponse = ref(false)
const isLoadingMessages = ref(false)
const uploadInFlightCount = ref(0)
const uploadedDocuments = ref([])
const selectedUploadedDocuments = ref([])
const isUploadingDocument = computed(() => {
  if (uploadInFlightCount.value > 0) {
    return true
  }

  return selectedUploadedDocuments.value.some(
    item => getBradleyAttachmentStatus(item) === BRADLEY_ATTACHMENT_STATUS.UPLOADING,
  )
})
const hasAttachmentsNotReady = computed(
  () => !areAllBradleyAttachmentsReadyForSend(selectedUploadedDocuments.value),
)
/** Temporarily pause REMOVE_FILE events while we clear selections in code. */
const suppressFileRemovalAnalytics = ref(false)

const streamingContent = ref('')
const isStreaming = ref(false)
// The step Bradley is on right now. Replaced in place, never accumulated: the consultant
// needs to know what is happening, not a transcript of what happened. Cleared the moment the
// answer text starts, because the text is the closure of the step that produced it.
const currentStep = ref(null)
const streamingCitations = ref([])
const streamAbortController = ref(null)

function handleStopStream() {
  streamAbortController.value?.abort()
}

const BRADLEY_SETTINGS_STORAGE_KEY = 'bradley_settings'
const defaultBradleySettings = () => ({ maxDocuments: 20, similarityThreshold: 0.17 })

function loadBradleySettings() {
  try {
    const raw = typeof window !== 'undefined' && window.localStorage?.getItem(BRADLEY_SETTINGS_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (typeof parsed?.maxDocuments === 'number' && typeof parsed?.similarityThreshold === 'number') {
        return { maxDocuments: parsed.maxDocuments, similarityThreshold: parsed.similarityThreshold }
      }
    }
  }
  catch {
    // ignore
  }
  return defaultBradleySettings()
}

const bradleySettings = ref(loadBradleySettings())

function revokeAttachmentPreviewUrls(items) {
  for (const item of items || []) {
    const previewUrl = item?.preview_url || item?.previewUrl
    if (typeof previewUrl === 'string' && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl)
    }
  }
}

function resetConversationFilesState() {
  clearPendingBlobTransformations()
  revokeAttachmentPreviewUrls(selectedUploadedDocuments.value)
  suppressFileRemovalAnalytics.value = true
  uploadedFile.value = null
  uploadedDocuments.value = []
  selectedUploadedDocuments.value = []
  nextTick(() => {
    suppressFileRemovalAnalytics.value = false
  })
}

function clearSelectedUploadedDocumentsSilently() {
  clearPendingBlobTransformations()
  revokeAttachmentPreviewUrls(selectedUploadedDocuments.value)
  suppressFileRemovalAnalytics.value = true
  selectedUploadedDocuments.value = []
  nextTick(() => {
    suppressFileRemovalAnalytics.value = false
  })
}

function getConversationDisplayTitle(conversation) {
  if (!conversation || typeof conversation !== 'object') {
    return ''
  }

  return conversation.title || conversation.name || ''
}

function normalizeConversationActionPayload(payload, defaultSource = 'search_chats') {
  if (typeof payload === 'string') {
    return { conversationId: payload, source: defaultSource }
  }

  return {
    conversationId: payload?.conversationId || payload?.conversation_id || payload?.id || '',
    source: payload?.source || defaultSource,
  }
}

function getCurrentTitleForConversationId(id) {
  if (id === conversationId.value) {
    const active = activeConversationTitle.value?.trim() ?? ''
    if (active) {
      return active
    }
  }
  const row = searchConversations.value.find(
    conversation => (conversation.conversation_id || conversation.id) === id,
  )
  return (row ? getConversationDisplayTitle(row) : '').trim()
}

function syncConversationTitleInList(conversationIdToSync, nextTitle) {
  searchConversations.value = searchConversations.value.map((conversation) => {
    const id = conversation.conversation_id || conversation.id
    if (id !== conversationIdToSync) {
      return conversation
    }

    return {
      ...conversation,
      title: nextTitle,
      updated_at: new Date().toISOString(),
      user_defined_title: true,
      is_title_overridden: true,
      is_auto_title: false,
    }
  })
}

function upsertConversationInList(conversationSummary) {
  const id = conversationSummary?.conversation_id || conversationSummary?.id
  if (!id) {
    return
  }

  const nextConversation = {
    ...conversationSummary,
    conversation_id: conversationSummary.conversation_id || id,
    title: getConversationDisplayTitle(conversationSummary) || activeConversationTitle.value,
    updated_at: conversationSummary.updated_at || new Date().toISOString(),
  }

  const existingIndex = searchConversations.value.findIndex((conversation) => {
    const conversationIdToCompare = conversation.conversation_id || conversation.id
    return conversationIdToCompare === id
  })

  if (existingIndex === -1) {
    searchConversations.value = [nextConversation, ...searchConversations.value]
    return
  }

  searchConversations.value = searchConversations.value.map((conversation, index) => {
    if (index !== existingIndex) {
      return conversation
    }
    return { ...conversation, ...nextConversation }
  })
}

function handlePromptSelect(payload) {
  const promptText = typeof payload === 'string' ? payload : (payload?.text ?? '')
  const icebreakerId = typeof payload === 'object' && payload != null ? payload.icebreakerId : null
  const icebreakerPosition = typeof payload === 'object' && payload != null ? payload.icebreakerPosition : null

  if (icebreakerId && typeof icebreakerPosition === 'number') {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.ICEBREAKER_CLICKED, {
      icebreaker_id: icebreakerId,
      icebreaker_position: icebreakerPosition,
      icebreaker_text: promptText,
      source_component: 'icebreaker_section',
    })
  }

  if (DATA_ANALYSIS_ENABLED && promptText === t('newBradley.promptCards.prompt2')) {
    handleToolSelect(ANALYSE_DATA_TOOL)
  }
  selectedPrompt.value = promptText
}

async function handleSend(message) {
  const trimmedMessage = typeof message === 'string' ? message.trim() : ''
  const readyAttachments = getReadySelectedAttachments()
  const hasAttachments = readyAttachments.length > 0

  if (
    (!trimmedMessage && !hasAttachments)
    || isStreaming.value
    || isAwaitingResponse.value
    || isUploadingDocument.value
    || hasAttachmentsNotReady.value
  ) {
    return
  }

  trackEvent(BRADLEY_ANALYTICS_EVENTS.SEND_PROMPT_CLICKED, {
    has_files_attached: hasAttachments,
    file_count: readyAttachments.length,
    prompt_length: trimmedMessage.length,
    tools_selected: bradleyToolsSelectedForAnalytics(selectedTools.value),
  })

  lastMessage.value = trimmedMessage

  if (!uploadedFile.value) {
    uploadedFile.value = null
  }

  selectedPrompt.value = ''

  const analyseDataEnabled = selectedTools.value.includes(ANALYSE_DATA_TOOL)
  const linkingToProjectId = !conversationId.value && activeProjectId.value
    ? activeProjectId.value
    : null

  if (!conversationId.value) {
    resetConversationFilesState()
    try {
      const response = await startBradleyConversation(trimmedMessage || 'Conversation', {
        useGenie: analyseDataEnabled,
      })
      conversationId.value = response?.conversation_id || null
      activeConversationTitle.value = response?.title || trimmedMessage

      if (!conversationId.value) {
        return
      }

      if (linkingToProjectId) {
        try {
          const linkResult = await (await requireProjectsBundle()).linkBradleyConversationToProject(
            linkingToProjectId,
            conversationId.value,
          )
          trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_CHAT_CREATED, (await requireProjectsBundle()).bradleyProjectChatAnalyticsPayload({
            action: 'created',
            projectId: linkingToProjectId,
            chatId: conversationId.value,
            userId: authStore?.userData?.id ?? null,
            triggerSource: 'project_composer',
          }))
          projectWorkspaceReference.value?.upsertConversation?.({
            conversation_id: conversationId.value,
            title: activeConversationTitle.value,
            updated_at: new Date().toISOString(),
            project_conversation_id: linkResult?.project_conversation_id || null,
            project_id: linkingToProjectId,
          })
        }
        catch (linkError) {
          console.error('Failed to link Bradley conversation to project', linkError)
          conversationId.value = null
          activeConversationTitle.value = ''
          clearStoredConversationId()
          toast.add({
            severity: 'error',
            summary: t('newBradley.toasts.linkProjectChatFailed'),
            detail: linkError?.message ?? String(linkError),
            life: 5000,
            group: 'bradley',
          })
          return
        }
      }

      upsertConversationInList({
        ...response,
        conversation_id: conversationId.value,
        title: activeConversationTitle.value,
      })
    }
    catch (error) {
      console.error('Failed to start Bradley conversation', error)
      return
    }
  }

  if (!conversationId.value) {
    return
  }

  showChatFile.value = true

  const temporaryMessageId = `temp-${Date.now()}`

  if (activeProjectId.value) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_PROMPT_SUBMITTED, (await requireProjectsBundle()).bradleyProjectPromptAnalyticsPayload({
      action: 'prompt_submitted',
      projectId: activeProjectId.value,
      chatId: conversationId.value,
      userId: authStore?.userData?.id ?? null,
      promptId: temporaryMessageId,
      triggerSource: linkingToProjectId ? 'project_composer' : 'project_chat_thread',
      status: 'submitted',
    }))
  }

  trackEvent(BRADLEY_ANALYTICS_EVENTS.SEND_PROMPT, {
    prompt_length: trimmedMessage.length,
    selected_tools_count: selectedTools.value.length,
    selected_documents_count: readyAttachments.length,
    embedded: props.embedded,
  })

  messages.value = [
    ...messages.value,
    {
      message_id: temporaryMessageId,
      is_user: true,
      content: trimmedMessage,
      timestamp: new Date().toISOString(),
      agent_type: 'user',
      selected_documents_metadata: readyAttachments.map(d => ({
        file_name: d.file_name,
        file_hash: getBradleyAttachmentFileHashForSend(d),
        ...(d.file_id ? { file_id: d.file_id } : {}),
      })),
    },
  ]

  const selectedDocumentsMetadata = readyAttachments.map(d => ({
    file_name: d.file_name,
    file_hash: getBradleyAttachmentFileHashForSend(d),
    ...(d.file_id ? { file_id: d.file_id } : {}),
  }))
  clearSelectedUploadedDocumentsSilently()

  const abortController = new AbortController()
  streamAbortController.value = abortController
  isAwaitingResponse.value = true
  streamingContent.value = ''
  currentStep.value = null
  streamingCitations.value = []

  try {
    await streamBradleyMessage(
      conversationId.value,
      {
        content: trimmedMessage,
        useGenie: analyseDataEnabled,
        indexesToSearch: selectedTools.value,
        hierarchicalSearch: true,
        k: bradleySettings.value.maxDocuments,
        cosineSimilarityThreshold: bradleySettings.value.similarityThreshold,
        selectedDocumentsMetadata,
      },
      {
        signal: abortController.signal,
        onChunk(chunk) {
          if (isAwaitingResponse.value) {
            isAwaitingResponse.value = false
            isStreaming.value = true
          }
          currentStep.value = null
          streamingContent.value += chunk
        },
        onCitation(citation) {
          streamingCitations.value = [...streamingCitations.value, citation]
        },
        onStep(step) {
          // Replaces whatever was there. A failure shows until the answer text arrives, which
          // is the moment the answer itself explains what happened -- what must not happen is
          // the previous line going on describing work that stopped.
          currentStep.value = step
        },
        onFinal(metadata) {
          const assistantMessage = {
            ...buildBradleyAssistantMessage(metadata, streamingContent.value),
            agent_type: metadata?.agent_type ?? 'assistant',
            selected_documents_metadata: selectedDocumentsMetadata,
          }
          messages.value = [
            ...messages.value,
            assistantMessage,
          ]
          streamingContent.value = ''
          currentStep.value = null
          streamingCitations.value = []
          isStreaming.value = false
          streamAbortController.value = null

          if (activeProjectId.value) {
            runWithProjectsBundle((projects) => {
              trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_RESPONSE_GENERATED, projects.bradleyProjectPromptAnalyticsPayload({
                action: 'response_generated',
                projectId: activeProjectId.value,
                chatId: conversationId.value,
                userId: authStore?.userData?.id ?? null,
                promptId: temporaryMessageId,
                responseId: assistantMessage.message_id || null,
                triggerSource: 'project_chat_thread',
                status: 'generated',
              }))
            })
          }
        },
      },
    )
  }
  catch (error) {
    if (error?.name === 'AbortError') {
      if (streamingContent.value) {
        messages.value = [
          ...messages.value,
          {
            message_id: `assistant-partial-${Date.now()}`,
            is_user: false,
            content: streamingContent.value,
            timestamp: new Date().toISOString(),
            documents: [],
            agent_type: 'assistant',
          },
        ]
      }
    }
    else {
      console.error('Failed to stream Bradley message', error)
      if (streamingContent.value) {
        messages.value = [
          ...messages.value,
          {
            message_id: `assistant-error-${Date.now()}`,
            is_user: false,
            content: `${streamingContent.value}\n\n*An error occurred. The response may be incomplete. Please try again.*`,
            timestamp: new Date().toISOString(),
            documents: [],
            agent_type: 'assistant',
          },
        ]
      }
      else {
        messages.value = messages.value.filter(m => !m.message_id?.startsWith('temp-'))
      }
    }
    streamingContent.value = ''
    currentStep.value = null
    streamingCitations.value = []
    streamAbortController.value = null
  }
  finally {
    isAwaitingResponse.value = false
    isStreaming.value = false
  }
}

let conversationBootstrapPromise = null
const pendingBlobTransformations = new Map()
let blobTransformationPollPromise = null

function clearPendingBlobTransformations() {
  pendingBlobTransformations.clear()
}

function isClientAttachmentStillSelected(clientId) {
  if (!clientId) {
    return true
  }
  return selectedUploadedDocuments.value.some(item => item?.client_id === clientId)
}

function isPendingAttachmentStillSelected(fileId, pending) {
  if (pending?.clientId) {
    return isClientAttachmentStillSelected(pending.clientId)
  }
  return selectedUploadedDocuments.value.some(
    item => item?.file_id != null && String(item.file_id) === String(fileId),
  )
}

function prunePendingBlobTransformations() {
  for (const [fileId, pending] of [...pendingBlobTransformations.entries()]) {
    if (!isPendingAttachmentStillSelected(fileId, pending)) {
      pendingBlobTransformations.delete(fileId)
    }
  }
}

function rememberConversationUpload(document_) {
  if (!document_?.file_id && !document_?.file_hash) {
    return
  }

  uploadedDocuments.value = [
    ...uploadedDocuments.value.filter((item) => {
      if (document_.file_id && item?.file_id && String(item.file_id) === String(document_.file_id)) {
        return false
      }
      if (document_.file_hash && item?.file_hash && String(item.file_hash) === String(document_.file_hash)) {
        return false
      }
      return true
    }),
    document_,
  ]
}

function sleepMs(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function applyBlobTransformationResult(fileId, entry, pending) {
  if (!isPendingAttachmentStillSelected(fileId, pending)) {
    pendingBlobTransformations.delete(String(fileId))
    return
  }

  const transformationStatus = typeof entry === 'string'
    ? entry
    : entry?.transformation_status
  const fileHashMd = typeof entry === 'object' && entry != null
    ? (entry.file_hash_md || null)
    : null
  const isReady = transformationStatus === 'ready'
  const patch = {
    transformation_status: transformationStatus,
    upload_status: isReady
      ? BRADLEY_ATTACHMENT_STATUS.SUCCESS
      : BRADLEY_ATTACHMENT_STATUS.ERROR,
  }

  if (fileHashMd) {
    patch.file_hash_md = fileHashMd
  }

  if (pending?.clientId) {
    patchSelectedAttachmentByClientId(pending.clientId, patch)
  }
  else {
    selectedUploadedDocuments.value = selectedUploadedDocuments.value.map((item) => {
      if (String(item?.file_id) !== String(fileId)) {
        return item
      }
      return { ...item, ...patch }
    })
  }

  if (isReady) {
    const enriched = selectedUploadedDocuments.value.find(
      item => String(item?.file_id) === String(fileId)
        || (pending?.clientId && item?.client_id === pending.clientId),
    )
    if (enriched) {
      uploadedFile.value = enriched
      rememberConversationUpload(enriched)
    }

    if (pending?.meta) {
      const elapsed = (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())
        - (pending.startedAtPerf || pending.startedAt || Date.now())
      const uploadDurationMs = Math.max(0, Math.round(elapsed))
      const fileSizeKb = Math.max(0, Math.round((pending.fileSize || 0) / 1024))

      trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_SUCCESS, {
        upload_method: pending.uploadMethod || 'button',
        file_count: 1,
        file_size_kb: fileSizeKb,
        upload_duration_ms: uploadDurationMs,
      })
      trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE_SUCCESS, {
        ...pending.meta,
      })
    }
  }
  else if (pending?.meta) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
      upload_method: pending.uploadMethod || 'button',
      error_type: 'transformation_failed',
      file_count: 1,
    })
    trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE_FAIL, {
      ...pending.meta,
      stage: 'blob_transformation',
      error_category: 'transformation_failed',
    })
  }
}

function maybeStartBlobTransformationPoller() {
  if (uploadInFlightCount.value > 0) {
    return
  }
  if (pendingBlobTransformations.size === 0) {
    return
  }
  void ensureBlobTransformationPoller()
}

async function ensureBlobTransformationPoller() {
  if (blobTransformationPollPromise) {
    return blobTransformationPollPromise
  }
  if (uploadInFlightCount.value > 0 || pendingBlobTransformations.size === 0) {
    return null
  }

  blobTransformationPollPromise = (async () => {
    try {
      while (pendingBlobTransformations.size > 0 && conversationId.value) {
        if (uploadInFlightCount.value > 0) {
          break
        }

        prunePendingBlobTransformations()
        const ids = [...pendingBlobTransformations.keys()]
        if (!ids.length) {
          break
        }

        let files = []
        try {
          const statusPayload = await fetchBradleyBlobTransformationStatus(conversationId.value, ids)
          files = Array.isArray(statusPayload.files) ? statusPayload.files : []
        }
        catch (error) {
          console.error('Failed to fetch Bradley blob transformation status', error)
          if (pendingBlobTransformations.size > 0 && uploadInFlightCount.value === 0) {
            await sleepMs(BRADLEY_BLOB_TRANSFORMATION_POLL_INTERVAL_MS)
          }
          continue
        }

        prunePendingBlobTransformations()

        const byId = new Map(
          files
            .filter(file => file?.file_id != null)
            .map(file => [String(file.file_id), file]),
        )

        for (const fileId of [...pendingBlobTransformations.keys()]) {
          const pending = pendingBlobTransformations.get(fileId)
          if (!pending || !isPendingAttachmentStillSelected(fileId, pending)) {
            pendingBlobTransformations.delete(fileId)
            continue
          }

          const entry = byId.get(fileId)
          const status = entry?.transformation_status || 'requested'

          if (status === 'ready') {
            applyBlobTransformationResult(fileId, entry, pending)
            pendingBlobTransformations.delete(fileId)
            continue
          }

          if (status === 'failed') {
            applyBlobTransformationResult(fileId, entry || { transformation_status: 'failed' }, pending)
            pendingBlobTransformations.delete(fileId)
          }
        }

        if (pendingBlobTransformations.size > 0 && uploadInFlightCount.value === 0) {
          await sleepMs(BRADLEY_BLOB_TRANSFORMATION_POLL_INTERVAL_MS)
        }
      }
    }
    finally {
      blobTransformationPollPromise = null
      maybeStartBlobTransformationPoller()
    }
  })()

  return blobTransformationPollPromise
}

function registerPendingBlobTransformation(fileId, context = {}) {
  const id = fileId != null ? String(fileId) : ''
  if (!id) {
    return
  }
  if (!isPendingAttachmentStillSelected(id, context)) {
    return
  }

  pendingBlobTransformations.set(id, {
    ...context,
    startedAt: context.startedAt || Date.now(),
    startedAtPerf: context.startedAtPerf
      ?? (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()),
  })
}

watch(selectedUploadedDocuments, () => {
  prunePendingBlobTransformations()
}, { deep: true })

async function ensureConversationForUpload(seedTitle = 'File Upload Conversation') {
  if (conversationId.value) {
    return conversationId.value
  }

  if (conversationBootstrapPromise) {
    return conversationBootstrapPromise
  }

  conversationBootstrapPromise = (async () => {
    try {
      const analyseDataEnabled = selectedTools.value.includes(ANALYSE_DATA_TOOL)
      const linkingToProjectId = activeProjectId.value
      const response = await startBradleyConversation(seedTitle, {
        useGenie: analyseDataEnabled,
      })
      conversationId.value = response?.conversation_id || null
      activeConversationTitle.value = response?.title || seedTitle

      if (!conversationId.value) {
        return null
      }

      if (linkingToProjectId) {
        const linkResult = await (await requireProjectsBundle()).linkBradleyConversationToProject(
          linkingToProjectId,
          conversationId.value,
        )
        trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_CHAT_CREATED, (await requireProjectsBundle()).bradleyProjectChatAnalyticsPayload({
          action: 'created',
          projectId: linkingToProjectId,
          chatId: conversationId.value,
          userId: authStore?.userData?.id ?? null,
          triggerSource: 'project_composer',
        }))
        projectWorkspaceReference.value?.upsertConversation?.({
          conversation_id: conversationId.value,
          title: activeConversationTitle.value,
          updated_at: new Date().toISOString(),
          project_conversation_id: linkResult?.project_conversation_id || null,
          project_id: linkingToProjectId,
        })
      }

      upsertConversationInList({
        ...response,
        conversation_id: conversationId.value,
        title: activeConversationTitle.value,
      })
      return conversationId.value
    }
    finally {
      conversationBootstrapPromise = null
    }
  })()

  return conversationBootstrapPromise
}

async function addSuccessfulUploadToProjectFiles(uploadedDocument, { fallbackFile = null } = {}) {
  if (!activeProjectId.value || !uploadedDocument) {
    return
  }
  if (getBradleyAttachmentStatus(uploadedDocument) === BRADLEY_ATTACHMENT_STATUS.ERROR) {
    return
  }
  if (!uploadedDocument.file_id && !uploadedDocument.document_id && !uploadedDocument.id) {
    return
  }

  const projects = await requireProjectsBundle()
  const projectFile = projects.normalizeBradleyProjectFile({
    file_id: uploadedDocument.file_id || uploadedDocument.document_id || uploadedDocument.id,
    conversation_id: conversationId.value,
    file_name: uploadedDocument.file_name || uploadedDocument.name || fallbackFile?.name,
    file_size: uploadedDocument.file_size ?? uploadedDocument.size ?? fallbackFile?.size,
    mime_type: uploadedDocument.mime_type || fallbackFile?.type || null,
    content_type: uploadedDocument.content_type || uploadedDocument.mime_type || fallbackFile?.type || null,
    file_hash: uploadedDocument.file_hash || null,
    file_hash_md: uploadedDocument.file_hash_md || null,
    transformation_status: uploadedDocument.transformation_status || null,
    updated_at: new Date().toISOString(),
    created_at: uploadedDocument.created_at || new Date().toISOString(),
    source: 'chat',
  }, activeProjectId.value)

  if (!projectFile) {
    return
  }

  projects.upsertCachedBradleyProjectFile(activeProjectId.value, projectFile)
  projectFilesRevision.value += 1
  projectWorkspaceReference.value?.upsertProjectFile?.(projectFile)
  trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_FILE_UPLOADED, {
    action: 'uploaded',
    project_id: activeProjectId.value,
    file_id: projectFile.file_id,
    file_size: projectFile.file_size,
    file_type: bradleyExtensionFromFileName(projectFile.file_name),
    user_id: authStore?.userData?.id ?? null,
    event_timestamp: new Date().toISOString(),
    source: showChatFile.value ? 'project_chat_thread' : 'project_composer',
  })
}

async function handleFileUploaded(payload) {
  const file = payload?.file ?? payload
  const uploadMethod = payload?.uploadMethod === 'drag_and_drop'
    ? 'drag_and_drop'
    : (payload?.uploadMethod === 'paste' ? 'paste' : 'button')
  const clientId = payload?.clientId || null

  if (typeof File === 'undefined' || !(file instanceof File)) {
    return
  }

  try {
    await nextTick()
    await ensureConversationForUpload(file.name || 'File Upload Conversation')
    if (!conversationId.value) {
      if (clientId) {
        patchSelectedAttachmentByClientId(clientId, {
          upload_status: BRADLEY_ATTACHMENT_STATUS.ERROR,
        })
      }
      return
    }

    const uploadedDocument = await uploadBradleyDocumentFromFile(file, { uploadMethod, clientId })
    await addSuccessfulUploadToProjectFiles(uploadedDocument, { fallbackFile: file })

    if (
      activeProjectId.value
      && uploadedDocument
      && getBradleyAttachmentStatus(uploadedDocument) !== BRADLEY_ATTACHMENT_STATUS.ERROR
    ) {
      trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_PROMPT_FILE_ATTACHED, (await requireProjectsBundle()).bradleyProjectPromptAnalyticsPayload({
        action: 'file_attached',
        projectId: activeProjectId.value,
        chatId: conversationId.value,
        userId: authStore?.userData?.id ?? null,
        triggerSource: showChatFile.value ? 'project_chat_thread' : 'project_composer',
        status: 'uploaded',
      }))
    }
  }
  catch (error) {
    console.error('Failed to upload Bradley document from composer', error)
    if (clientId && isClientAttachmentStillSelected(clientId)) {
      patchSelectedAttachmentByClientId(clientId, {
        upload_status: BRADLEY_ATTACHMENT_STATUS.ERROR,
      })
    }
  }
}

async function loadMessages() {
  if (!conversationId.value) {
    return
  }

  try {
    isLoadingMessages.value = true
    const response = await fetchBradleyMessages(conversationId.value)
    messages.value = Array.isArray(response) ? response : []
  }
  catch (error) {
    console.error('Failed to load Bradley messages', error)
  }
  finally {
    isLoadingMessages.value = false
  }
}

async function loadUploadedDocuments() {
  if (!conversationId.value) {
    return
  }

  try {
    const response = await fetchBradleyUploadedDocuments(conversationId.value)
    uploadedDocuments.value = Array.isArray(response) ? response : []
    uploadedFile.value = uploadedDocuments.value.length > 0
      ? uploadedDocuments.value[uploadedDocuments.value.length - 1]
      : null
  }
  catch (error) {
    console.error('Failed to load Bradley uploaded documents', error)
    uploadedDocuments.value = []
  }
}

const TOOL_LABEL_ALIASES = { analyzeData: ANALYSE_DATA_TOOL }

async function handleToolSelect(toolLabel) {
  const normalizedLabel = TOOL_LABEL_ALIASES[toolLabel] ?? toolLabel
  if (!ALLOWED_TOOLS.includes(normalizedLabel)) {
    return
  }

  const index = selectedTools.value.indexOf(normalizedLabel)
  if (index === -1) {
    selectedTools.value = [normalizedLabel]
    trackEvent(BRADLEY_ANALYTICS_EVENTS.SELECT_TOOLS, { tool: normalizedLabel })
    if (activeProjectId.value) {
      const projects = await requireProjectsBundle()
      trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_PROMPT_TOOL_SELECTED, projects.bradleyProjectPromptAnalyticsPayload({
        action: 'tool_selected',
        projectId: activeProjectId.value,
        chatId: conversationId.value,
        userId: authStore?.userData?.id ?? null,
        toolName: normalizedLabel,
        triggerSource: showChatFile.value ? 'project_chat_thread' : 'project_composer',
        status: 'selected',
      }))
    }
  }
}

function handleRemoveTool(toolLabel) {
  const index = selectedTools.value.indexOf(toolLabel)
  if (index !== -1) {
    selectedTools.value.splice(index, 1)
    trackEvent(BRADLEY_ANALYTICS_EVENTS.REMOVE_TOOLS, { tool: toolLabel })
  }
}

function handleShowToast(toastConfig) {
  const summary = toastConfig.summaryKey ? t(toastConfig.summaryKey) : toastConfig.summary
  const detail = toastConfig.detailKey
    ? t(toastConfig.detailKey, toastConfig.detailParams || {})
    : toastConfig.detail
  const { summaryKey, detailKey, detailParams, ...rest } = toastConfig
  const message = {
    ...rest,
    summary,
    detail,
    group: 'bradley',
  }
  if (toastConfig.reportContent) {
    message.styleClass = 'bradley-toast-report-container bg-green-50'
    message.contentStyleClass = 'flex justify-between items-start w-full gap-2'
  }
  toast.add(message)
}

function pickUploadedDocumentForNewUpload(fileToUpload, uploadResponse, documents) {
  const list = Array.isArray(documents) ? documents : []
  if (uploadResponse && typeof uploadResponse === 'object') {
    const responseId = uploadResponse.file_hash ?? uploadResponse.id ?? uploadResponse.document_id
    if (responseId !== undefined && responseId !== null && `${responseId}`.length > 0) {
      const matchById = list.find(
        document_ =>
          document_
          && (document_.file_hash === responseId
            || document_.id === responseId
            || document_.document_id === responseId),
      )
      if (matchById) {
        return matchById
      }
    }
  }
  const sameName = list.filter(document_ => document_ && document_.file_name === fileToUpload.name)
  if (sameName.length > 0) {
    return sameName[sameName.length - 1]
  }
  return list.length > 0 ? list[list.length - 1] : null
}

function mapBlobUploadToAttachment(uploadResponse, fileToUpload) {
  if (!uploadResponse || typeof uploadResponse !== 'object') {
    return null
  }

  return {
    file_id: uploadResponse.file_id,
    file_hash: uploadResponse.file_hash,
    file_name: uploadResponse.file_name || fileToUpload?.name,
    content_type: uploadResponse.content_type,
    file_extension: uploadResponse.file_extension,
    size_bytes: uploadResponse.size_bytes,
    status: uploadResponse.status,
    blob_url: uploadResponse.blob_url,
    blob_name: uploadResponse.blob_name,
  }
}

function isSameSelectedAttachment(existing, incoming) {
  if (!existing || !incoming) {
    return false
  }

  if (incoming.client_id && existing.client_id === incoming.client_id) {
    return true
  }

  if (incoming.file_hash && existing.file_hash === incoming.file_hash) {
    return true
  }

  if (incoming.file_id && existing.file_id === incoming.file_id) {
    return true
  }

  return false
}

function patchSelectedAttachmentByClientId(clientId, patch) {
  if (!clientId) {
    return
  }

  selectedUploadedDocuments.value = selectedUploadedDocuments.value.map((item) => {
    if (item?.client_id !== clientId) {
      return item
    }
    return {
      ...item,
      ...patch,
    }
  })
}

function getReadySelectedAttachments() {
  return selectedUploadedDocuments.value.filter(isBradleyAttachmentReadyForSend)
}

async function uploadBradleyDocumentFromFile(file, { uploadMethod = 'button', clientId = null } = {}) {
  if (typeof File === 'undefined' || !(file instanceof File) || !conversationId.value) {
    return null
  }

  if (NEW_FILE_UPLOAD_ENABLED) {
    return uploadBradleyBlobDocumentFromFile(file, { uploadMethod, clientId })
  }

  return uploadBradleyLegacyDocumentFromFile(file, { uploadMethod, clientId })
}

async function uploadBradleyLegacyDocumentFromFile(file, { uploadMethod = 'button', clientId = null } = {}) {
  const fileToUpload = file
  const meta = bradleyFileTelemetryMeta(fileToUpload)
  const t0 = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()

  trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_STARTED, {
    upload_method: uploadMethod,
    file_count: 1,
  })
  trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE, {
    ...meta,
    embedded: props.embedded,
  })

  const payload = {
    file: fileToUpload,
    file_name: fileToUpload.name,
  }

  try {
    uploadInFlightCount.value += 1

    trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOADING_FILE, {
      ...meta,
    })

    if (clientId) {
      patchSelectedAttachmentByClientId(clientId, {
        upload_status: BRADLEY_ATTACHMENT_STATUS.UPLOADING,
      })
    }

    const uploadResponse = await uploadBradleyDocument(conversationId.value, payload)

    if (clientId && !isClientAttachmentStillSelected(clientId)) {
      return null
    }

    await loadUploadedDocuments()
    const newDocument = pickUploadedDocumentForNewUpload(
      fileToUpload,
      uploadResponse,
      uploadedDocuments.value,
    )

    const enrichedDocument = newDocument
      ? {
          ...newDocument,
          client_id: clientId || newDocument.client_id || null,
          size: fileToUpload.size,
          file_size: fileToUpload.size,
          upload_status: BRADLEY_ATTACHMENT_STATUS.SUCCESS,
        }
      : null

    if (!enrichedDocument) {
      throw new Error('Legacy document upload did not return a document')
    }

    if (clientId && !isClientAttachmentStillSelected(clientId)) {
      return null
    }

    uploadedFile.value = enrichedDocument

    if (clientId) {
      if (isClientAttachmentStillSelected(clientId)) {
        patchSelectedAttachmentByClientId(clientId, enrichedDocument)
      }
    }
    else if (!selectedUploadedDocuments.value.some(d => isSameSelectedAttachment(d, enrichedDocument))) {
      selectedUploadedDocuments.value = [...selectedUploadedDocuments.value, enrichedDocument]
    }

    const elapsed = (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - t0
    const uploadDurationMs = Math.max(0, Math.round(elapsed))
    const fileSizeKb = Math.max(0, Math.round(fileToUpload.size / 1024))

    trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_SUCCESS, {
      upload_method: uploadMethod,
      file_count: 1,
      file_size_kb: fileSizeKb,
      upload_duration_ms: uploadDurationMs,
    })
    trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE_SUCCESS, {
      ...meta,
    })

    return enrichedDocument
  }
  catch (uploadError) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
      upload_method: uploadMethod,
      error_type: bradleyUploadErrorAnalyticsType(uploadError),
      file_count: 1,
    })
    trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE_FAIL, {
      ...meta,
      stage: 'document_upload',
      error_category: bradleyAnalyticsErrorCategory(uploadError),
    })

    if (clientId && isClientAttachmentStillSelected(clientId)) {
      patchSelectedAttachmentByClientId(clientId, {
        upload_status: BRADLEY_ATTACHMENT_STATUS.ERROR,
      })
    }
    else if (!clientId) {
      uploadedFile.value = null
    }
    throw uploadError
  }
  finally {
    uploadInFlightCount.value = Math.max(0, uploadInFlightCount.value - 1)
  }
}

async function uploadBradleyBlobDocumentFromFile(file, { uploadMethod = 'button', clientId = null } = {}) {
  const fileToUpload = file
  const meta = bradleyFileTelemetryMeta(fileToUpload)
  const t0 = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
  const treatAsImage = isBradleyImageFile(fileToUpload, true)

  trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_STARTED, {
    upload_method: uploadMethod,
    file_count: 1,
  })
  trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE, {
    ...meta,
    embedded: props.embedded,
  })

  const payload = {
    file: fileToUpload,
    file_name: fileToUpload.name,
  }

  try {
    uploadInFlightCount.value += 1

    trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOADING_FILE, {
      ...meta,
    })

    if (clientId) {
      patchSelectedAttachmentByClientId(clientId, {
        upload_status: BRADLEY_ATTACHMENT_STATUS.UPLOADING,
      })
    }

    const blobUploadResponse = await uploadBradleyBlobStorageFile(conversationId.value, payload)
    const fileId = blobUploadResponse?.file_id
    if (!fileId || blobUploadResponse?.status !== 'uploaded') {
      throw new Error('Blob upload did not return uploaded status with file_id')
    }

    if (clientId && !isClientAttachmentStillSelected(clientId)) {
      return null
    }

    if (blobUploadResponse.transformation_status === 'failed') {
      throw new Error('Blob upload transformation request failed')
    }

    const previewUrl = (() => {
      if (clientId) {
        const existing = selectedUploadedDocuments.value.find(item => item?.client_id === clientId)
        if (existing?.preview_url) {
          return existing.preview_url
        }
      }
      return treatAsImage || isBradleyImageFile(fileToUpload, true)
        ? URL.createObjectURL(fileToUpload)
        : null
    })()

    const pendingAttachment = {
      ...mapBlobUploadToAttachment(blobUploadResponse, fileToUpload),
      client_id: clientId || null,
      is_image: treatAsImage || isBradleyImageFile(fileToUpload, true),
      preview_url: previewUrl,
      size: fileToUpload.size,
      file_size: fileToUpload.size,
      upload_status: BRADLEY_ATTACHMENT_STATUS.UPLOADING,
      transformation_status: blobUploadResponse.transformation_status || 'requested',
    }

    if (clientId) {
      if (!isClientAttachmentStillSelected(clientId)) {
        return null
      }
      patchSelectedAttachmentByClientId(clientId, pendingAttachment)
    }
    else if (!selectedUploadedDocuments.value.some(d => isSameSelectedAttachment(d, pendingAttachment))) {
      selectedUploadedDocuments.value = [...selectedUploadedDocuments.value, pendingAttachment]
    }

    const transformationStatus = blobUploadResponse.transformation_status || 'requested'
    if (transformationStatus === 'ready' && blobUploadResponse.file_hash) {
      const enrichedDocument = {
        ...pendingAttachment,
        transformation_status: transformationStatus,
        file_hash: blobUploadResponse.file_hash,
        ...(blobUploadResponse.file_hash_md
          ? { file_hash_md: blobUploadResponse.file_hash_md }
          : {}),
        upload_status: BRADLEY_ATTACHMENT_STATUS.SUCCESS,
      }

      if (clientId && !isClientAttachmentStillSelected(clientId)) {
        return null
      }

      uploadedFile.value = enrichedDocument
      rememberConversationUpload(enrichedDocument)

      if (clientId) {
        patchSelectedAttachmentByClientId(clientId, enrichedDocument)
      }
      else if (!selectedUploadedDocuments.value.some(d => isSameSelectedAttachment(d, enrichedDocument))) {
        selectedUploadedDocuments.value = [...selectedUploadedDocuments.value, enrichedDocument]
      }

      const elapsed = (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - t0
      const uploadDurationMs = Math.max(0, Math.round(elapsed))
      const fileSizeKb = Math.max(0, Math.round(fileToUpload.size / 1024))

      trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_SUCCESS, {
        upload_method: uploadMethod,
        file_count: 1,
        file_size_kb: fileSizeKb,
        upload_duration_ms: uploadDurationMs,
      })
      trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE_SUCCESS, {
        ...meta,
      })

      return enrichedDocument
    }

    registerPendingBlobTransformation(fileId, {
      clientId,
      uploadMethod,
      meta,
      fileSize: fileToUpload.size,
      startedAtPerf: t0,
    })

    return pendingAttachment
  }
  catch (uploadError) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
      upload_method: uploadMethod,
      error_type: bradleyUploadErrorAnalyticsType(uploadError),
      file_count: 1,
    })
    trackEvent(BRADLEY_ANALYTICS_EVENTS.UPLOAD_FILE_FAIL, {
      ...meta,
      stage: 'blob_storage_upload',
      error_category: bradleyAnalyticsErrorCategory(uploadError),
    })

    if (clientId && isClientAttachmentStillSelected(clientId)) {
      patchSelectedAttachmentByClientId(clientId, {
        upload_status: BRADLEY_ATTACHMENT_STATUS.ERROR,
        transformation_status: 'failed',
      })
    }
    else if (!clientId) {
      uploadedFile.value = null
    }
    throw uploadError
  }
  finally {
    uploadInFlightCount.value = Math.max(0, uploadInFlightCount.value - 1)
    maybeStartBlobTransformationPoller()
  }
}

function exitChatFile() {
  streamAbortController.value?.abort()
  streamAbortController.value = null
  streamingContent.value = ''
  isStreaming.value = false
  isAwaitingResponse.value = false
  showChatFile.value = false
  lastMessage.value = ''
  selectedPrompt.value = ''
  selectedTools.value = []
  resetConversationFilesState()
  messages.value = []
  conversationId.value = null
  activeConversationTitle.value = ''
  clearStoredConversationId()
}

function clearActiveProject() {
  activeProjectId.value = null
  activeProjectTitle.value = ''
  activeProjectUpdatedAt.value = null
  activeProjectIsOwner.value = false
  autoEditProjectId.value = null
}

function handleProjectAutoEditConsumed() {
  autoEditProjectId.value = null
}

function handleProjectLoaded(payload) {
  if (!payload?.projectId || payload.projectId !== activeProjectId.value) {
    return
  }

  const project = payload.project
  if (project) {
    if (typeof project.title === 'string' && project.title.trim()) {
      activeProjectTitle.value = project.title.trim()
    }
    if (project.updated_at || project.created_at) {
      activeProjectUpdatedAt.value = project.updated_at || project.created_at
    }
  }

  if (typeof payload.isOwner === 'boolean') {
    activeProjectIsOwner.value = payload.isOwner
  }

  if (Array.isArray(payload.files)) {
    projectFilesRevision.value += 1
  }
}

async function handleProjectChatSelection(payload) {
  const conversation = payload?.conversation
  const selectedConversationId = conversation?.conversation_id || conversation?.id

  if (!selectedConversationId) {
    return
  }

  trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_CHAT_OPENED, (await requireProjectsBundle()).bradleyProjectChatAnalyticsPayload({
    action: 'opened',
    projectId: activeProjectId.value,
    chatId: selectedConversationId,
    userId: authStore?.userData?.id ?? null,
    triggerSource: 'project_chats_tab',
  }))

  conversationId.value = selectedConversationId
  activeConversationTitle.value = getConversationDisplayTitle(conversation)
  storeConversationId(selectedConversationId)
  showChatFile.value = true
  await Promise.all([
    loadMessages(),
    loadUploadedDocuments(),
  ])
}

async function handleProjectChatRename(payload) {
  const originalOnSettled = typeof payload?.onSettled === 'function' ? payload.onSettled : null
  const conversationIdToRename = payload?.conversationId
  const nextTitle = payload?.title?.trim?.() || ''

  await handleConversationTitleRename({
    ...payload,
    source: 'project_chats',
    onSettled: (error) => {
      if (!error && conversationIdToRename && nextTitle) {
        projectWorkspaceReference.value?.syncConversationTitle?.(conversationIdToRename, nextTitle)
      }
      originalOnSettled?.(error ?? null)
    },
  })
}

async function handleProjectChatRemove(payload) {
  const conversationIdToRemove = payload?.conversationId
  const onSettled = typeof payload?.onSettled === 'function' ? payload.onSettled : null
  const projectId = activeProjectId.value

  if (!conversationIdToRemove || !projectId) {
    onSettled?.(new Error('Invalid unlink payload'))
    return
  }

  try {
    await (await requireProjectsBundle()).unlinkBradleyConversationFromProject(projectId, conversationIdToRemove)
    projectWorkspaceReference.value?.removeConversation?.(conversationIdToRemove)

    if (conversationIdToRemove === conversationId.value) {
      exitChatFile()
    }

    onSettled?.()
  }
  catch (error) {
    console.error('Failed to unlink Bradley conversation from project', error)
    toast.add({
      severity: 'error',
      summary: t('newBradley.toasts.deleteProjectChatFailed'),
      detail: error?.message ?? String(error),
      life: 4000,
      group: 'bradley',
    })
    onSettled?.(error instanceof Error ? error : new Error(String(error)))
  }
}

async function handleCreateProject() {
  if (!PROJECTS_ENABLED || isCreatingProject.value) {
    return
  }

  isCreatingProject.value = true

  try {
    const projectCount = sidebarMenuReference.value?.getProjectsCount?.() ?? 0
    const temporaryTitle = (await requireProjectsBundle()).generateTemporaryBradleyProjectName(t, projectCount)
    const created = await (await requireProjectsBundle()).createBradleyProject(temporaryTitle)
    const projectId = created?.project_id

    if (!projectId) {
      throw new Error('Project creation response missing project_id')
    }

    const createdTitle = typeof created?.title === 'string' && created.title.trim()
      ? created.title.trim()
      : temporaryTitle

    activeProjectId.value = projectId
    activeProjectTitle.value = createdTitle
    activeProjectUpdatedAt.value = created?.updated_at || created?.created_at || new Date().toISOString()
    activeProjectIsOwner.value = true
    autoEditProjectId.value = projectId
    exitChatFile()

    const listItem = (await requireProjectsBundle()).buildBradleyProjectListItem(created)
    if (listItem) {
      sidebarMenuReference.value?.prependProject?.(listItem)
    }
    else {
      await sidebarMenuReference.value?.reloadProjects?.()
    }

    trackEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_CREATED, (await requireProjectsBundle()).bradleyProjectManagementAnalyticsPayload({
      action: 'create',
      projectId,
      newProjectName: createdTitle,
      triggerSource: 'bradley_sidebar',
      userId: authStore?.userData?.id ?? null,
    }))
  }
  catch (error) {
    console.error('Failed to create Bradley project', error)
    toast.add({
      severity: 'error',
      summary: t('newBradley.toasts.createProjectFailed'),
      detail: error?.message ?? String(error),
      life: 5000,
      group: 'bradley',
    })
  }
  finally {
    isCreatingProject.value = false
  }
}

async function handleProjectTitleRename(payload) {
  const onSettled = typeof payload?.onSettled === 'function' ? payload.onSettled : null
  function notifySettled(error) {
    onSettled?.(error ?? null)
  }

  const requestedProjectId = payload?.projectId || activeProjectId.value
  const nextTitle = payload?.title?.trim?.() || ''

  if (!requestedProjectId || !nextTitle) {
    notifySettled(new Error('Invalid project rename payload'))
    return
  }

  const currentTitle = requestedProjectId === activeProjectId.value
    ? activeProjectTitle.value.trim()
    : (payload?.previousTitle?.trim?.() || '')

  if (nextTitle === currentTitle) {
    notifySettled()
    return
  }

  const previousTitle = activeProjectTitle.value

  if (requestedProjectId === activeProjectId.value) {
    activeProjectTitle.value = nextTitle
  }
  sidebarMenuReference.value?.syncProjectTitle?.(requestedProjectId, nextTitle)

  try {
    const result = await (await requireProjectsBundle()).updateBradleyProject(requestedProjectId, { title: nextTitle })
    if (result?.updated === false) {
      throw new Error(t('newBradley.projectWorkspace.renameErrorRequest'))
    }
    if (requestedProjectId === activeProjectId.value) {
      activeProjectUpdatedAt.value = new Date().toISOString()
    }
    notifySettled()
  }
  catch (error) {
    console.error('Failed to rename Bradley project', error)
    if (requestedProjectId === activeProjectId.value) {
      activeProjectTitle.value = previousTitle
    }
    sidebarMenuReference.value?.syncProjectTitle?.(requestedProjectId, previousTitle)
    toast.add({
      severity: 'error',
      summary: t('newBradley.toasts.renameProjectFailed'),
      detail: error?.message ?? String(error),
      life: 4000,
      group: 'bradley',
    })
    notifySettled(error instanceof Error ? error : new Error(String(error)))
  }
}

async function handleArchiveProject(payload) {
  const onSettled = typeof payload?.onSettled === 'function' ? payload.onSettled : null
  function notifySettled(error) {
    onSettled?.(error ?? null)
  }

  const projectId = payload?.projectId
  if (!projectId) {
    notifySettled(new Error('Invalid project archive payload'))
    return
  }

  try {
    const projects = await requireProjectsBundle()
    await projects.archiveBradleyProject(projectId)
    sidebarMenuReference.value?.removeProject?.(projectId)
    projects.clearCachedBradleyProjectFiles(projectId)

    if (activeProjectId.value === projectId) {
      clearActiveProject()
      exitChatFile()
    }

    toast.add({
      severity: 'success',
      summary: t('newBradley.toasts.archiveProjectSuccess'),
      life: 3000,
      group: 'bradley',
    })

    notifySettled()
  }
  catch (error) {
    console.error('Failed to archive Bradley project', error)
    toast.add({
      severity: 'error',
      summary: t('newBradley.toasts.archiveProjectFailed'),
      detail: error?.message ?? String(error),
      life: 5000,
      group: 'bradley',
    })
    notifySettled(error instanceof Error ? error : new Error(String(error)))
  }
}

function handleNewChat() {
  clearActiveProject()
  exitChatFile()
}

async function handleProjectSelection(payload) {
  if (!PROJECTS_ENABLED) {
    return
  }

  const projectId = payload?.projectId
  if (!projectId) {
    return
  }

  const projects = await requireProjectsBundle()
  autoEditProjectId.value = null
  activeProjectId.value = projectId
  activeProjectTitle.value = projects.getBradleyProjectTitle(payload?.project)
  activeProjectUpdatedAt.value = projects.getBradleyProjectUpdatedAt(payload?.project)
  activeProjectIsOwner.value = projects.isBradleyProjectOwner(payload?.project)
  exitChatFile()
}

function handleEndChat() {
  uiStore.closeBradleyPanel()
  exitChatFile()
}

function handleMinimizeDrawer() {
  uiStore.minimizeBradleyPanel()
}

watch(
  () => uiStore.bradleyPanelStartFreshSession,
  (startFresh) => {
    if (!props.embedded || !startFresh || !uiStore.bradleyPanelOpen) {
      return
    }
    handleNewChat()
    uiStore.consumeBradleyPanelStartFreshSession()
  },
)

function handleSidebarWidthChange(width) {
  sidebarWidth.value = typeof width === 'number' ? width : DEFAULT_SIDEBAR_WIDTH
}

function openTermsOfServiceModal() {
  termsOfServiceModalOpen.value = true
}

function closeTermsOfServiceModal() {
  termsOfServiceModalOpen.value = false
  setCookie(TERMS_COOKIE_NAME, 'true', 365)
}

function openAiPolicyModal() {
  aiPolicyModalOpen.value = true
}

function closeAiPolicyModal() {
  aiPolicyModalOpen.value = false
}

function toggleBodyScroll(isLocked) {
  if (typeof document === 'undefined') {
    return
  }

  document.body.style.overflow = isLocked ? 'hidden' : ''
}

const anyTermsPanelOpen = computed(() => termsOfServiceModalOpen.value || aiPolicyModalOpen.value)

watch(anyTermsPanelOpen, (isOpen) => {
  toggleBodyScroll(isOpen)
})

onUnmounted(() => {
  toggleBodyScroll(false)
  window.removeEventListener('keydown', handleBradleySearchShortcut)
})

const bradleyPageStyle = computed(() => ({
  '--terms-panel-width': anyTermsPanelOpen.value ? TERMS_PANEL_WIDTH : '0px',
  '--sidebar-width': `${sidebarWidth.value}px`,
}))

function openSettingsModal() {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.SETTINGS, { surface: 'settings_modal' })
  settingsModalOpen.value = true
}

function closeSettingsModal() {
  settingsModalOpen.value = false
}

function handleUpdateSettings(payload) {
  if (payload && typeof payload.maxDocuments === 'number' && typeof payload.similarityThreshold === 'number') {
    bradleySettings.value = { maxDocuments: payload.maxDocuments, similarityThreshold: payload.similarityThreshold }
    try {
      window.localStorage?.setItem(BRADLEY_SETTINGS_STORAGE_KEY, JSON.stringify(bradleySettings.value))
    }
    catch {
      // ignore
    }
  }
}

const isClearingAllConversations = ref(false)

function openSearchModal() {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.SEARCH_CHATS, { surface: 'search_modal' })
  searchModalOpen.value = true
  loadUserConversations()
}

/** Abre o modal de histórico / busca fora do menu lateral (atalho padrão Cmd/Ctrl+K). */
function handleBradleySearchShortcut(event) {
  if (!(event.ctrlKey || event.metaKey))
    return
  if (event.key !== 'k' && event.key !== 'K')
    return
  const element = event.target
  if (element instanceof HTMLElement && element.closest('input, textarea, [contenteditable="true"]'))
    return
  event.preventDefault()
  openSearchModal()
}

async function handleClearAllConversations() {
  if (isClearingAllConversations.value) {
    return
  }
  try {
    isClearingAllConversations.value = true
    const result = await clearAllBradleyConversations()
    exitChatFile()
    searchConversations.value = []
    const count = result?.count ?? 0
    toast.add({
      severity: 'success',
      summary: t('newBradley.toasts.clearAllSuccess'),
      detail: t('newBradley.toasts.clearAllDetail', { count }),
      life: 4000,
      group: 'bradley',
    })
    closeSettingsModal()
    closeSearchModal()
  }
  catch (error) {
    console.error('Failed to clear all Bradley conversations', error)
    toast.add({
      severity: 'error',
      summary: t('newBradley.toasts.clearAllFailed'),
      detail: error?.message ?? String(error),
      life: 5000,
      group: 'bradley',
    })
  }
  finally {
    isClearingAllConversations.value = false
  }
}

function closeSearchModal() {
  searchModalOpen.value = false
}

function openUserManualModal() {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.FAQ, { surface: 'faq_modal' })
  userManualModalOpen.value = true
  faqOpenSection.value = '1'
}

function closeUserManualModal() {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.FAQ_CLOSE, { surface: 'faq_modal' })
  userManualModalOpen.value = false
  faqOpenSection.value = null
}

watch(faqOpenSection, (section) => {
  if (!section || !userManualModalOpen.value)
    return

  const sectionNumber = Number(section)
  const eventName = BRADLEY_FAQ_VIEW_QUESTION_BY_SECTION[sectionNumber]
  if (eventName) {
    trackEvent(eventName, { surface: 'faq_modal', question_number: sectionNumber })
  }
})

async function handleSearchConversationSelection(conversation) {
  if (!conversation) {
    closeSearchModal()
    return
  }

  const selectedConversationId = conversation.conversation_id || conversation.id

  if (!selectedConversationId) {
    closeSearchModal()
    return
  }

  conversationId.value = selectedConversationId
  activeConversationTitle.value = getConversationDisplayTitle(conversation)
  storeConversationId(selectedConversationId)
  showChatFile.value = true
  closeSearchModal()
  await Promise.all([
    loadMessages(),
    loadUploadedDocuments(),
  ])
}

async function handleRecentConversationSelection(payload) {
  const conversation = payload?.conversation
  const selectedConversationId = conversation?.conversation_id || conversation?.id

  if (selectedConversationId) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.RECENT_CHAT_CLICKED, {
      chat_id: selectedConversationId,
      chat_position: payload?.position ?? 0,
      source: 'recent_chats',
    })
  }

  clearActiveProject()
  await handleSearchConversationSelection(conversation)
}

async function loadUserConversations() {
  try {
    isLoadingSearchConversations.value = true
    const conversations = await fetchBradleyConversationsByUser()
    searchConversations.value = conversations
    if (conversationId.value) {
      const current = conversations.find((conversation) => {
        const id = conversation.conversation_id || conversation.id
        return id === conversationId.value
      })
      if (current) {
        activeConversationTitle.value = getConversationDisplayTitle(current)
      }
    }
  }
  catch (error) {
    console.error('Failed to load Bradley conversations', error)
    searchConversations.value = []
  }
  finally {
    isLoadingSearchConversations.value = false
  }
}

async function handleRemoveConversation(payload) {
  const { conversationId: conversationIdToRemove, source } = normalizeConversationActionPayload(payload)

  if (!conversationIdToRemove) {
    return
  }

  let deletionSucceeded = false

  try {
    isLoadingSearchConversations.value = true
    await deleteBradleyConversation(conversationIdToRemove)
    deletionSucceeded = true

    searchConversations.value = searchConversations.value.filter((conversation) => {
      const id = conversation.conversation_id || conversation.id
      return id !== conversationIdToRemove
    })

    if (conversationIdToRemove === conversationId.value) {
      exitChatFile()
    }
  }
  catch (error) {
    console.error('Failed to delete Bradley conversation', error)
  }
  finally {
    isLoadingSearchConversations.value = false
  }

  if (deletionSucceeded) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.CHAT_DELETE_CONFIRMED, {
      chat_id: conversationIdToRemove,
      source,
    })
    await loadUserConversations()
  }
}

async function handleDeleteActiveConversation() {
  if (!conversationId.value || isDeletingConversation.value) {
    return
  }

  const activeConversationId = conversationId.value

  try {
    isDeletingConversation.value = true
    await deleteBradleyConversation(activeConversationId)

    searchConversations.value = searchConversations.value.filter((conversation) => {
      const id = conversation.conversation_id || conversation.id
      return id !== activeConversationId
    })
    projectWorkspaceReference.value?.removeConversation?.(activeConversationId)

    exitChatFile()
    await loadUserConversations()
  }
  catch (error) {
    console.error('Failed to delete active Bradley conversation', error)
  }
  finally {
    isDeletingConversation.value = false
  }
}

async function handleConversationTitleRename(payload) {
  const onSettled = typeof payload?.onSettled === 'function' ? payload.onSettled : null
  function notifySettled(error) {
    onSettled?.(error ?? null)
  }

  const requestedConversationId = payload?.conversationId || conversationId.value
  const nextTitle = payload?.title?.trim?.() || ''

  if (!requestedConversationId || !nextTitle) {
    notifySettled(new Error('Invalid rename payload'))
    return
  }

  const currentTitle = getCurrentTitleForConversationId(requestedConversationId)
  if (nextTitle === currentTitle) {
    notifySettled()
    return
  }

  const previousTitle = requestedConversationId === conversationId.value
    ? activeConversationTitle.value
    : ''

  const foundConversation = searchConversations.value.find(
    conversation => (conversation.conversation_id || conversation.id) === requestedConversationId,
  )
  const previousListEntry = foundConversation ? { ...foundConversation } : null

  if (requestedConversationId === conversationId.value) {
    activeConversationTitle.value = nextTitle
  }
  syncConversationTitleInList(requestedConversationId, nextTitle)

  try {
    await renameBradleyConversationTitle(requestedConversationId, nextTitle)
    trackEvent(BRADLEY_ANALYTICS_EVENTS.CHAT_RENAMED, {
      chat_id: requestedConversationId,
      previous_name_length: currentTitle.length,
      new_name_length: nextTitle.length,
    })
    notifySettled()
  }
  catch (error) {
    console.error('Failed to rename Bradley conversation', error)
    if (requestedConversationId === conversationId.value) {
      activeConversationTitle.value = previousTitle
    }
    if (previousListEntry) {
      searchConversations.value = searchConversations.value.map((conversation) => {
        const id = conversation.conversation_id || conversation.id
        if (id !== requestedConversationId) {
          return conversation
        }
        return { ...previousListEntry }
      })
    }
    try {
      await loadUserConversations()
    }
    catch {
    }
    toast.add({
      severity: 'error',
      summary: t('newBradley.toasts.renameFailed'),
      detail: error?.message ?? String(error),
      life: 4000,
      group: 'bradley',
    })
    notifySettled(error instanceof Error ? error : new Error(String(error)))
  }
}

function handleConversationRenameInitiated(payload) {
  const { conversationId: initiatedConversationId, source } = normalizeConversationActionPayload(payload, 'recent_chats')
  if (!initiatedConversationId) {
    return
  }

  trackEvent(BRADLEY_ANALYTICS_EVENTS.CHAT_RENAME_INITIATED, {
    chat_id: initiatedConversationId,
    source,
  })
}

function handleConversationDeleteInitiated(payload) {
  const { conversationId: initiatedConversationId, source } = normalizeConversationActionPayload(payload, 'recent_chats')
  if (!initiatedConversationId) {
    return
  }

  trackEvent(BRADLEY_ANALYTICS_EVENTS.CHAT_DELETE_INITIATED, {
    chat_id: initiatedConversationId,
    source,
  })
}

function handleConversationDeleteCanceled(payload) {
  const { conversationId: canceledConversationId, source } = normalizeConversationActionPayload(payload, 'recent_chats')
  if (!canceledConversationId) {
    return
  }

  trackEvent(BRADLEY_ANALYTICS_EVENTS.CHAT_DELETE_CANCELED, {
    chat_id: canceledConversationId,
    source,
  })
}

function ensureNewChatFromQuery() {
  if (route.query[NEW_CHAT_QUERY] === '1') {
    clearStoredConversationId()
    handleNewChat()
    router.replace({ name: route.name, query: {} })
  }
}

function bootstrapBradleySession() {
  ensureNewChatFromQuery()
  if (route.query[NEW_CHAT_QUERY] === '1') {
    loadUserConversations()
    return
  }

  const storedConversationId = getStoredConversationId()
  if (storedConversationId) {
    conversationId.value = storedConversationId
    showChatFile.value = true
    loadMessages()
    loadUploadedDocuments()
    loadUserConversations()
    return
  }

  loadUserConversations()
}

onMounted(() => {
  window.addEventListener('keydown', handleBradleySearchShortcut)

  if (PROJECTS_ENABLED) {
    void loadProjectsBundle()
  }

  if (props.embedded && uiStore.bradleyPanelStartFreshSession) {
    handleNewChat()
    uiStore.consumeBradleyPanelStartFreshSession()
    loadUserConversations()
    return
  }

  if (!props.embedded) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.ASK_BRADLEY_AI_BUTTON, { source: 'bradley_ai_route' })
  }

  bootstrapBradleySession()
})

watch(() => route.query[NEW_CHAT_QUERY], (value) => {
  if (value === '1') {
    ensureNewChatFromQuery()
  }
})

watch(
  () => uiStore.bradleyPanelOpen,
  (isOpen) => {
    if (!isOpen || conversationId.value) {
      return
    }
    showChatFile.value = false
    resetConversationFilesState()
  },
)

watch(
  selectedUploadedDocuments,
  (next, previous) => {
    if (suppressFileRemovalAnalytics.value) {
      return
    }
    const previousDocuments = Array.isArray(previous) ? previous : []
    const list = Array.isArray(next) ? next : []
    if (previousDocuments.length === 0 || list.length >= previousDocuments.length) {
      return
    }
    const kept = new Set(list.map(d => d?.file_hash).filter(Boolean))
    for (const document_ of previousDocuments) {
      if (document_?.file_hash && !kept.has(document_.file_hash)) {
        trackEvent(BRADLEY_ANALYTICS_EVENTS.REMOVE_FILE, {
          file_extension: bradleyExtensionFromFileName(document_.file_name),
        })
      }
    }
  },
  { deep: true },
)
</script>

<template>
  <div
    class="bradley-page"
    :class="[
      { 'bradley-page--terms-open': anyTermsPanelOpen },
      { 'bradley-page--embedded': props.embedded },
    ]"
    :style="bradleyPageStyle"
  >
    <dss-shell-module-content
      v-if="!props.embedded"
      background="bg-dss-surface-50"
    >
      <div class="bradley-layout">
        <div class="bradley-layout__left">
          <header
            v-if="!props.embedded"
            class="bradley-page__header-wrap"
          >
            <dss-module-title :title="t('core.menu.main.bradley-ai')" />
          </header>

          <div class="bradley-container">
            <div
              v-if="showChatFile"
              class="chat-file-wrapper"
              :class="{ 'chat-file-wrapper--project': isProjectWorkspaceOpen }"
            >
              <header
                v-if="isProjectWorkspaceOpen"
                class="bradley-project-chat-header"
              >
                <button
                  type="button"
                  class="bradley-project-chat-header__back"
                  :aria-label="t('newBradley.projectWorkspace.back')"
                  :title="t('newBradley.projectWorkspace.back')"
                  @click="exitChatFile"
                >
                  <i
                    class="pi pi-chevron-left"
                    aria-hidden="true"
                  />
                  <span class="bradley-project-chat-header__title">
                    {{ activeProjectTitle || t('newBradley.sidebar.unnamedProject') }}
                  </span>
                </button>
              </header>
              <chat-file
                v-model:selected-uploaded-documents="selectedUploadedDocuments"
                :file="uploadedFile"
                :message="lastMessage"
                :messages="messages"
                :selected-tools="selectedTools"
                :loading-messages="isLoadingMessages"
                :awaiting-response="isAwaitingResponse"
                :is-streaming="isStreaming"
                :streaming-content="streamingContent"
                :current-step="currentStep"
                :streaming-citations="streamingCitations"
                :conversation-id="conversationId"
                :project-id="activeProjectId"
                :project-title="activeProjectTitle"
                :project-files="activeProjectFiles"
                :deleting="isDeletingConversation"
                :uploaded-documents="uploadedDocuments"
                :uploading="isUploadingDocument"
                @select-tool="handleToolSelect"
                @remove-tool="handleRemoveTool"
                @exit="exitChatFile"
                @send="handleSend"
                @file-uploaded="handleFileUploaded"
                @stop-stream="handleStopStream"
                @delete="handleDeleteActiveConversation"
                @show-toast="handleShowToast"
              />
            </div>
            <div
              v-else-if="showProjectWorkspace"
              class="bradley-content bradley-content--project"
            >
              <bradley-project-workspace
                ref="projectWorkspaceReference"
                v-model:selected-uploaded-documents="selectedUploadedDocuments"
                :project-id="activeProjectId"
                :title="activeProjectTitle"
                :updated-at="activeProjectUpdatedAt"
                :is-owner="activeProjectIsOwner"
                :active-conversation-id="conversationId"
                :resolve-conversation-id="ensureConversationForUpload"
                :selected-tools="selectedTools"
                :uploading="isUploadingDocument"
                :uploaded-documents="uploadedDocuments"
                @back="clearActiveProject"
                @rename-project="handleProjectTitleRename"
                @archive-project="handleArchiveProject"
                @select-chat="handleProjectChatSelection"
                @rename-chat="handleProjectChatRename"
                @remove-chat="handleProjectChatRemove"
                @project-loaded="handleProjectLoaded"
                @send="handleSend"
                @select-tool="handleToolSelect"
                @remove-tool="handleRemoveTool"
                @file-uploaded="handleFileUploaded"
              />
            </div>
            <div
              v-else
              class="bradley-content"
            >
              <div class="bradley-home-composer">
                <bradley-header />

                <div class="bradley-home-composer__input-group">
                  <bradley-input-area
                    v-model="selectedPrompt"
                    v-model:selected-uploaded-documents="selectedUploadedDocuments"
                    :selected-tools="selectedTools"
                    :uploading="isUploadingDocument"
                    :uploaded-documents="uploadedDocuments"
                    :conversation-id="conversationId"
                    :resolve-conversation-id="ensureConversationForUpload"
                    @send="handleSend"
                    @select-tool="handleToolSelect"
                    @remove-tool="handleRemoveTool"
                    @file-uploaded="handleFileUploaded"
                    @show-toast="handleShowToast"
                  />
                  <bradley-prompt-cards @select-prompt="handlePromptSelect" />
                </div>
              </div>
            </div>
          </div>
          <bradley-legal-disclaimer-bar
            @open-terms-of-service="openTermsOfServiceModal"
            @open-ai-policy="openAiPolicyModal"
          />
        </div>

        <bradley-sidebar-menu
          ref="sidebarMenuReference"
          class="bradley-sidebar"
          :conversations="searchConversations"
          :loading-conversations="sidebarRecentsLoading"
          :active-conversation-id="conversationId"
          :active-project-id="activeProjectId"
          :auto-edit-project-id="autoEditProjectId"
          @select-tool="handleToolSelect"
          @new-chat="handleNewChat"
          @open-settings="openSettingsModal"
          @open-user-manual="openUserManualModal"
          @select-conversation="handleRecentConversationSelection"
          @rename-conversation="handleConversationTitleRename"
          @rename-conversation-initiated="handleConversationRenameInitiated"
          @delete-conversation-initiated="handleConversationDeleteInitiated"
          @delete-conversation-canceled="handleConversationDeleteCanceled"
          @remove-conversation="handleRemoveConversation"
          @select-project="handleProjectSelection"
          @create-project="handleCreateProject"
          @rename-project="handleProjectTitleRename"
          @archive-project="handleArchiveProject"
          @auto-edit-consumed="handleProjectAutoEditConsumed"
          @sidebar-width-change="handleSidebarWidthChange"
        />
      </div>
    </dss-shell-module-content>

    <div
      v-else
      class="bradley-page__embedded-wrap"
    >
      <div class="bradley-layout">
        <div class="bradley-layout__left">
          <header class="bradley-page__embedded-header">
            <div class="bradley-page__embedded-header-title">
              <bradley-logo-icon
                class="bradley-page__embedded-logo"
                :height="20"
              />
            </div>
            <div class="bradley-page__embedded-header-actions">
              <button
                type="button"
                class="bradley-page__embedded-toolbar-btn"
                :aria-label="uiStore.bradleyPanelExpanded ? t('newBradley.panel.collapseFullscreen') : t('newBradley.panel.expandFullscreen')"
                :title="uiStore.bradleyPanelExpanded ? t('newBradley.panel.collapseFullscreen') : t('newBradley.panel.expandFullscreen')"
                @click="uiStore.toggleBradleyPanelExpanded()"
              >
                <i :class="uiStore.bradleyPanelExpanded ? 'pi pi-arrow-down-left-and-arrow-up-right-to-center' : 'pi pi-arrow-up-right-and-arrow-down-left-from-center'" />
              </button>
              <button
                type="button"
                class="bradley-page__embedded-toolbar-btn"
                :aria-label="t('newBradley.panel.minimizeDrawer')"
                :title="t('newBradley.panel.minimizeDrawer')"
                @click="handleMinimizeDrawer()"
              >
                <i class="pi pi-chevron-down" />
              </button>
              <button
                type="button"
                class="bradley-page__embedded-toolbar-btn bradley-page__embedded-toolbar-btn--end-chat"
                :aria-label="t('newBradley.panel.endChat')"
                :title="t('newBradley.panel.endChat')"
                @click="handleEndChat()"
              >
                <i
                  class="pi pi-times"
                  aria-hidden="true"
                />
                <span class="bradley-page__embedded-toolbar-btn__close-label">{{ t('newBradley.panel.endChat') }}</span>
              </button>
            </div>
          </header>
          <div class="bradley-container">
            <div
              v-if="showChatFile"
              class="chat-file-wrapper"
              :class="{ 'chat-file-wrapper--project': isProjectWorkspaceOpen }"
            >
              <header
                v-if="isProjectWorkspaceOpen"
                class="bradley-project-chat-header"
              >
                <button
                  type="button"
                  class="bradley-project-chat-header__back"
                  :aria-label="t('newBradley.projectWorkspace.back')"
                  :title="t('newBradley.projectWorkspace.back')"
                  @click="exitChatFile"
                >
                  <i
                    class="pi pi-chevron-left"
                    aria-hidden="true"
                  />
                  <span class="bradley-project-chat-header__title">
                    {{ activeProjectTitle || t('newBradley.sidebar.unnamedProject') }}
                  </span>
                </button>
              </header>
              <chat-file
                v-model:selected-uploaded-documents="selectedUploadedDocuments"
                :file="uploadedFile"
                :message="lastMessage"
                :messages="messages"
                :selected-tools="selectedTools"
                :loading-messages="isLoadingMessages"
                :awaiting-response="isAwaitingResponse"
                :is-streaming="isStreaming"
                :streaming-content="streamingContent"
                :current-step="currentStep"
                :streaming-citations="streamingCitations"
                :conversation-id="conversationId"
                :project-id="activeProjectId"
                :project-title="activeProjectTitle"
                :project-files="activeProjectFiles"
                :deleting="isDeletingConversation"
                :uploaded-documents="uploadedDocuments"
                :uploading="isUploadingDocument"
                @select-tool="handleToolSelect"
                @remove-tool="handleRemoveTool"
                @exit="exitChatFile"
                @send="handleSend"
                @file-uploaded="handleFileUploaded"
                @stop-stream="handleStopStream"
                @delete="handleDeleteActiveConversation"
                @show-toast="handleShowToast"
              />
            </div>
            <div
              v-else-if="showProjectWorkspace"
              class="bradley-content bradley-content--project"
            >
              <bradley-project-workspace
                ref="projectWorkspaceReference"
                v-model:selected-uploaded-documents="selectedUploadedDocuments"
                :project-id="activeProjectId"
                :title="activeProjectTitle"
                :updated-at="activeProjectUpdatedAt"
                :is-owner="activeProjectIsOwner"
                :active-conversation-id="conversationId"
                :resolve-conversation-id="ensureConversationForUpload"
                :selected-tools="selectedTools"
                :uploading="isUploadingDocument"
                :uploaded-documents="uploadedDocuments"
                @back="clearActiveProject"
                @rename-project="handleProjectTitleRename"
                @archive-project="handleArchiveProject"
                @select-chat="handleProjectChatSelection"
                @rename-chat="handleProjectChatRename"
                @remove-chat="handleProjectChatRemove"
                @project-loaded="handleProjectLoaded"
                @send="handleSend"
                @select-tool="handleToolSelect"
                @remove-tool="handleRemoveTool"
                @file-uploaded="handleFileUploaded"
              />
            </div>
            <div
              v-else
              class="bradley-content"
            >
              <div class="bradley-home-composer">
                <bradley-header />

                <div class="bradley-home-composer__input-group">
                  <bradley-input-area
                    v-model="selectedPrompt"
                    v-model:selected-uploaded-documents="selectedUploadedDocuments"
                    :selected-tools="selectedTools"
                    :uploading="isUploadingDocument"
                    :uploaded-documents="uploadedDocuments"
                    :conversation-id="conversationId"
                    :resolve-conversation-id="ensureConversationForUpload"
                    @send="handleSend"
                    @select-tool="handleToolSelect"
                    @remove-tool="handleRemoveTool"
                    @file-uploaded="handleFileUploaded"
                    @show-toast="handleShowToast"
                  />
                  <bradley-prompt-cards @select-prompt="handlePromptSelect" />
                </div>
              </div>
            </div>
          </div>
          <bradley-legal-disclaimer-bar
            @open-terms-of-service="openTermsOfServiceModal"
            @open-ai-policy="openAiPolicyModal"
          />
        </div>

        <bradley-sidebar-menu
          ref="sidebarMenuReference"
          class="bradley-sidebar"
          :conversations="searchConversations"
          :loading-conversations="sidebarRecentsLoading"
          :active-conversation-id="conversationId"
          :active-project-id="activeProjectId"
          :auto-edit-project-id="autoEditProjectId"
          @select-tool="handleToolSelect"
          @new-chat="handleNewChat"
          @open-settings="openSettingsModal"
          @open-user-manual="openUserManualModal"
          @select-conversation="handleRecentConversationSelection"
          @rename-conversation="handleConversationTitleRename"
          @rename-conversation-initiated="handleConversationRenameInitiated"
          @delete-conversation-initiated="handleConversationDeleteInitiated"
          @delete-conversation-canceled="handleConversationDeleteCanceled"
          @remove-conversation="handleRemoveConversation"
          @select-project="handleProjectSelection"
          @create-project="handleCreateProject"
          @rename-project="handleProjectTitleRename"
          @archive-project="handleArchiveProject"
          @auto-edit-consumed="handleProjectAutoEditConsumed"
          @sidebar-width-change="handleSidebarWidthChange"
        />
      </div>
    </div>

    <bradley-terms-of-service-modal
      :open="termsOfServiceModalOpen"
      :width="TERMS_PANEL_WIDTH"
      @close="closeTermsOfServiceModal"
    />

    <bradley-ai-policy-modal
      :open="aiPolicyModalOpen"
      :width="TERMS_PANEL_WIDTH"
      @close="closeAiPolicyModal"
    />

    <bradley-search-modal
      :open="searchModalOpen"
      :loading="isLoadingSearchConversations"
      :clearing-all="isClearingAllConversations"
      :conversations="searchConversations"
      @close="closeSearchModal"
      @select-conversation="handleSearchConversationSelection"
      @remove-conversation="handleRemoveConversation"
      @rename-conversation="handleConversationTitleRename"
      @rename-conversation-initiated="handleConversationRenameInitiated"
      @delete-conversation-initiated="handleConversationDeleteInitiated"
      @delete-conversation-canceled="handleConversationDeleteCanceled"
      @clear-all="handleClearAllConversations"
    />

    <bradley-settings-modal
      :open="settingsModalOpen"
      :max-documents="bradleySettings.maxDocuments"
      :similarity-threshold="bradleySettings.similarityThreshold"
      @close="closeSettingsModal"
      @update-settings="handleUpdateSettings"
    />

    <teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="transform opacity-0"
        enter-to-class="transform opacity-100"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="transform opacity-100"
        leave-to-class="transform opacity-0"
      >
        <div
          v-if="userManualModalOpen"
          class="fixed flex justify-end bg-white/70"
          style="z-index: 150; top: 0; left: 0; right: 0; bottom: 0;"
          @click.self="closeUserManualModal"
        >
          <div
            class="bg-white h-full flex flex-col shadow-2xl border-l border-dss-surface-200"
            :style="{ width: '640px', maxWidth: '100%' }"
          >
            <div class="flex items-center justify-between px-6 py-4 border-b border-dss-surface-200">
              <h2 class="font-hellix text-xl font-[450] leading-none">
                {{ $t('newBradley.faq.modalTitle') }}
              </h2>
              <button
                class="rounded-lg p-2 transition-colors hover:bg-gray-100"
                @click="closeUserManualModal"
              >
                <i class="pi pi-times text-lg text-gray-600" />
              </button>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-6">
              <Accordion
                v-model:value="faqOpenSection"
                class="faq-accordion"
              >
                <accordion-panel value="1">
                  <accordion-header class="font-bold text-[18px] leading-[130%] text-gray-900 cursor-pointer py-3 flex items-center justify-between w-full rounded-lg hover:bg-gray-50 transition-colors">
                    {{ $t('newBradley.faq.s1Title') }}
                  </accordion-header>
                  <accordion-content class="pt-0 pb-4">
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s1_1Q') }}
                    </h3>
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-4">
                      {{ $t('newBradley.faq.s1_1A') }}
                    </p>
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s1_2Q') }}
                    </h3>
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-2">
                      {{ $t('newBradley.faq.s1_2A') }}
                    </p>
                    <ul class="list-disc pl-6 mb-4 space-y-1">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s1_2_1') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s1_2_2') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s1_2_3') }}
                      </li>
                    </ul>
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s1_3Q') }}
                    </h3>
                    <ul class="list-disc pl-6 mb-0 space-y-2">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s1_3_1') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s1_3_2') }}
                      </li>
                    </ul>
                  </accordion-content>
                </accordion-panel>

                <accordion-panel value="2">
                  <accordion-header class="font-bold text-[18px] leading-[130%] text-gray-900 cursor-pointer py-3 flex items-center justify-between w-full rounded-lg hover:bg-gray-50 transition-colors">
                    {{ $t('newBradley.faq.s2Title') }}
                  </accordion-header>
                  <accordion-content class="pt-0 pb-4">
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-2">
                      {{ $t('newBradley.faq.s2Intro') }}
                    </p>
                    <ul class="list-disc pl-6 mb-0 space-y-2">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        <strong>{{ $t('newBradley.faq.s2TaskLabel') }}</strong> {{ $t('newBradley.faq.s2TaskDesc') }}
                        <ul class="list-none pl-4 mt-1 space-y-0.5">
                          <li>{{ $t('newBradley.faq.s2TaskEx1') }}</li>
                          <li>{{ $t('newBradley.faq.s2TaskEx2') }}</li>
                        </ul>
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        <strong>{{ $t('newBradley.faq.s2ContextLabel') }}</strong> {{ $t('newBradley.faq.s2ContextDesc') }}
                        <ul class="list-none pl-4 mt-1 space-y-0.5">
                          <li>{{ $t('newBradley.faq.s2ContextEx1') }}</li>
                          <li>{{ $t('newBradley.faq.s2ContextEx2') }}</li>
                        </ul>
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        <strong>{{ $t('newBradley.faq.s2FormatLabel') }}</strong> {{ $t('newBradley.faq.s2FormatDesc') }}
                        <ul class="list-none pl-4 mt-1 space-y-0.5">
                          <li>{{ $t('newBradley.faq.s2FormatEx1') }}</li>
                          <li>{{ $t('newBradley.faq.s2FormatEx2') }}</li>
                        </ul>
                      </li>
                    </ul>
                  </accordion-content>
                </accordion-panel>

                <accordion-panel value="3">
                  <accordion-header class="font-bold text-[18px] leading-[130%] text-gray-900 cursor-pointer py-3 flex items-center justify-between w-full rounded-lg hover:bg-gray-50 transition-colors">
                    {{ $t('newBradley.faq.s3Title') }}
                  </accordion-header>
                  <accordion-content class="pt-0 pb-4">
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s3_1Q') }}
                    </h3>
                    <ul class="list-disc pl-6 mb-4 space-y-1">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s3_1_1') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s3_1_2') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s3_1_3') }}
                      </li>
                    </ul>
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s3_2Q') }}
                    </h3>
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-2">
                      {{ $t('newBradley.faq.s3_2A') }}
                    </p>
                    <ul class="list-disc pl-6 mb-0 space-y-1">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s3_2_1') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s3_2_2') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s3_2_3') }}
                      </li>
                    </ul>
                  </accordion-content>
                </accordion-panel>

                <accordion-panel value="4">
                  <accordion-header class="font-bold text-[18px] leading-[130%] text-gray-900 cursor-pointer py-3 flex items-center justify-between w-full rounded-lg hover:bg-gray-50 transition-colors">
                    {{ $t('newBradley.faq.s4Title') }}
                  </accordion-header>
                  <accordion-content class="pt-0 pb-4">
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s4_1Q') }}
                    </h3>
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-2">
                      {{ $t('newBradley.faq.s4_1A') }}
                    </p>
                    <ul class="list-disc pl-6 mb-0 space-y-1">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s4_1_1') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s4_1_2') }}
                      </li>
                    </ul>
                  </accordion-content>
                </accordion-panel>

                <accordion-panel value="5">
                  <accordion-header class="font-bold text-[18px] leading-[130%] text-gray-900 cursor-pointer py-3 flex items-center justify-between w-full rounded-lg hover:bg-gray-50 transition-colors">
                    {{ $t('newBradley.faq.s5Title') }}
                  </accordion-header>
                  <accordion-content class="pt-0 pb-4">
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s5_1Q') }}
                    </h3>
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-4">
                      {{ $t('newBradley.faq.s5_1A') }}
                    </p>
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s5_2Q') }}
                    </h3>
                    <ul class="list-disc pl-6 mb-0 space-y-1">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s5_2_1') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s5_2_2') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s5_2_3') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s5_2_4') }}
                      </li>
                    </ul>
                  </accordion-content>
                </accordion-panel>

                <accordion-panel value="6">
                  <accordion-header class="font-bold text-[18px] leading-[130%] text-gray-900 cursor-pointer py-3 flex items-center justify-between w-full rounded-lg hover:bg-gray-50 transition-colors">
                    {{ $t('newBradley.faq.s6Title') }}
                  </accordion-header>
                  <accordion-content class="pt-0 pb-4">
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s6_1Q') }}
                    </h3>
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-2">
                      {{ $t('newBradley.faq.s6_1A') }}
                    </p>
                    <ul class="list-disc pl-6 mb-4 space-y-1">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s6_1_1') }}
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        {{ $t('newBradley.faq.s6_1_2') }}
                      </li>
                    </ul>
                    <h3 class="font-semibold text-[18px] leading-[130%] mb-2">
                      {{ $t('newBradley.faq.s6_2Q') }}
                    </h3>
                    <p class="font-normal text-[15px] leading-[140%] text-gray-700 mb-2">
                      {{ $t('newBradley.faq.s6_2A') }}
                    </p>
                    <ul class="list-disc pl-6 mb-0 space-y-1">
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        <strong>{{ $t('newBradley.faq.s6_2_1Label') }}</strong> {{ $t('newBradley.faq.s6_2_1Desc') }}
                        <ul class="list-none pl-4 mt-1 space-y-0.5">
                          <li>{{ $t('newBradley.faq.s6_2_1Yes') }}</li>
                          <li>{{ $t('newBradley.faq.s6_2_1No') }}</li>
                        </ul>
                      </li>
                      <li class="font-normal text-[15px] leading-[140%] text-gray-700">
                        <strong>{{ $t('newBradley.faq.s6_2_2Label') }}</strong> {{ $t('newBradley.faq.s6_2_2Desc') }}
                      </li>
                    </ul>
                  </accordion-content>
                </accordion-panel>
              </Accordion>
            </div>

            <div class="px-6 py-4 border-t border-dss-surface-200">
              <button
                class="w-full py-3 bg-dss-primary-500 hover:bg-dss-primary-600 text-white font-hellix font-medium rounded-lg transition-colors cursor-pointer"
                @click="closeUserManualModal"
              >
                {{ $t('newBradley.searchModal.close') }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <Toast
      position="top-right"
      group="bradley"
    >
      <template #message="{ message }">
        <div
          v-if="message.reportContent"
          class="min-w-0 flex-1 font-sans flex flex-col gap-1 text-left"
        >
          <div class="flex items-center gap-2 mb-1">
            <i
              class="pi pi-check-circle text-green-600 text-xl shrink-0"
              aria-hidden="true"
            />
            <span class="text-green-600 font-semibold text-base leading-normal">{{ message.summary }}</span>
          </div>
          <div class="font-medium text-sm leading-[1.63] text-neutral-900">
            {{ message.reportContent.main }}
          </div>
          <div class="font-normal text-xs leading-[1.63] text-neutral-900">
            {{ message.reportContent.secondary }}
          </div>
          <div class="text-xs leading-[1.63] text-green-600 bg-green-500/15 py-1 px-2 rounded-xl mt-1 inline-block w-fit">
            {{ message.reportContent.date }}
          </div>
        </div>
        <template v-else>
          <span class="font-bold mr-2">{{ message.summary }}</span>
          <span v-if="message.detail">{{ message.detail }}</span>
        </template>
      </template>
    </Toast>
  </div>
</template>

<style scoped>
.bradley-page {
  --terms-panel-width: 0px;
  --bradley-layout-padding-left: 32px;
  --bradley-layout-padding-right: 32px;
  --sidebar-width: 72px;
}

.bradley-page__header-wrap {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  box-sizing: border-box;
  height: 67px;
  min-height: 67px;
  padding: 0 32px;
  background-color: #ffffff;
}

.bradley-layout {
  display: grid;
  grid-template-columns: 1fr var(--sidebar-width);
  width: 100%;
  align-items: stretch;
}

.bradley-layout__left {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  align-self: stretch;
  overflow: hidden;
}

.bradley-page:not(.bradley-page--embedded) .bradley-layout {
  min-height: calc(100dvh - 7rem);
}

.bradley-page:not(.bradley-page--embedded) .bradley-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bradley-page:not(.bradley-page--embedded) .bradley-content {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.bradley-page:not(.bradley-page--embedded) .bradley-container > .chat-file-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bradley-page:not(.bradley-page--embedded) .bradley-container > .chat-file-wrapper :deep(.chat-file) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: auto;
}

.bradley-page:not(.bradley-page--embedded) .bradley-container > .chat-file-wrapper :deep(.chat-file__body) {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.bradley-page:not(.bradley-page--embedded) .bradley-container > .chat-file-wrapper :deep(.chat-file__messages) {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.bradley-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

.bradley-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 32px;
}

.bradley-content--project {
  gap: 0;
  align-items: stretch;
}

.chat-file-wrapper--project {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  min-height: 0;
  margin: 0;
  overflow: hidden;
  background: #fafafe;
}

.bradley-project-chat-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  width: 100%;
  padding: 32px 32px 0;
}

.bradley-project-chat-header__back {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  padding: 0;
  color: #111827;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.15s ease;
}

.bradley-project-chat-header__back:hover {
  color: #374151;
}

.bradley-project-chat-header__back i {
  flex-shrink: 0;
  color: #9ca3af;
  font-size: 0.875rem;
  line-height: 1;
}

.bradley-project-chat-header__back:hover i {
  color: #6b7280;
}

.bradley-project-chat-header__title {
  overflow: hidden;
  color: inherit;
  font-family: var(--font-inter, Inter, sans-serif);
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bradley-home-composer {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 48rem;
  margin: 0 auto;
  padding-top: 48px;
}

.bradley-home-composer__input-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.bradley-home-composer :deep(.input-area-container) {
  margin-top: 0;
}

.bradley-container > .chat-file-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bradley-sidebar {
  position: sticky;
  top: 0;
  height: 100vh;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bradley-page__embedded-wrap .bradley-sidebar {
  height: 100%;
  max-height: 100%;
  align-self: stretch;
}

@media screen and (max-width: 768px) {
  .bradley-layout {
    grid-template-columns: 1fr;
  }

  .bradley-sidebar {
    display: none;
  }
}

:deep(i[class*='pi-']) {
  font-family: 'primeicons' !important;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  display: inline-block;
  -webkit-font-smoothing: antialiased;
}

.bradley-page--terms-open .bradley-layout {
  padding-right: var(--terms-panel-width);
}

.bradley-page--embedded {
  height: 100%;
  overflow: hidden;
}

.bradley-page__embedded-wrap {
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fafafe;
}

.bradley-page__embedded-header {
  flex-shrink: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  height: 67px;
  min-height: 67px;
  padding: 0 1rem;
  border-bottom: 1px solid #e2e3ed;
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 5;
}

.bradley-page__embedded-header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.bradley-page__embedded-logo {
  color: #0000cc;
}

.bradley-page__embedded-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bradley-page__embedded-toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  min-height: 2.25rem;
  padding: 0.125rem;
  border: 1px solid #d8d9ec;
  border-radius: 0.375rem;
  background: #ffffff;
  color: var(--color-dss-primary-500);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.bradley-page__embedded-toolbar-btn:hover {
  background: #f4f4f8;
  border-color: #c5c7e0;
  color: var(--color-dss-primary-600, var(--color-dss-primary-500));
}

.bradley-page__embedded-toolbar-btn i {
  color: inherit;
}

.bradley-page__embedded-toolbar-btn--end-chat {
  gap: 0.375rem;
  min-width: auto;
  padding: 0.25rem 0.625rem;
}

.bradley-page__embedded-toolbar-btn__close-label {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.bradley-page__embedded-wrap .bradley-layout {
  flex: 1;
  min-height: 0;
  height: 0;
  align-items: stretch;
}

.bradley-page__embedded-wrap .bradley-layout__left {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.bradley-page__embedded-wrap .bradley-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-x: hidden;
}

.bradley-page__embedded-wrap .bradley-content {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 32px;
}

.bradley-page__embedded-wrap .bradley-container > .chat-file-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.faq-accordion :deep(.p-accordionheader),
.faq-accordion :deep(.p-accordionheader .p-accordionheader-content),
.faq-accordion :deep(.p-accordionheader .p-accordionheader-toggle-icon) {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.005em;
  color: var(--p-primary-950);
}

.faq-accordion h3 {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.005em;
  color: var(--p-primary-950);
}

.faq-accordion p,
.faq-accordion li {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: var(--p-primary-950);
}
</style>

<style>
.bradley-toast-report-container.p-toast-message {
  background: #f0fdf4;
}
</style>
